import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const HeroImageDownloadTrigger: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const downloadHeroImages = async () => {
    setIsRunning(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error } = await supabase.functions.invoke('admin-download-hero-images', {
        body: {}
      });

      if (error) {
        console.error('Error:', error);
        setError(error.message || 'Failed to download hero images');
        toast.error('Failed to download hero images: ' + (error.message || 'Unknown error'));
      } else {
        const processedCount = data?.processed || 0;
        setSuccess(`Hero image download completed successfully. Processed ${processedCount} images.`);
        toast.success(`Hero image download completed! Processed ${processedCount} images.`);
      }
    } catch (err: any) {
      console.error('Failed to download hero images:', err);
      setError(err.message || 'Failed to download hero images');
      toast.error('Failed to download hero images: ' + (err.message || 'Unknown error'));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Download Hero Images
        </CardTitle>
        <CardDescription>
          Download and process hero images for recipes that have external image URLs but no storage images.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button 
          onClick={downloadHeroImages} 
          disabled={isRunning}
          className="w-full"
        >
          {isRunning ? 'Downloading Images...' : 'Download Hero Images'}
        </Button>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm text-destructive">{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-800">{success}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};